import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, getDocs, query, orderBy, limit, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import Card from "../../components/Card";
import { getRecentAdminLogs, getLogTypeLabel, getLogTypeColor } from "../../services/adminLog";
import { AdminLog } from "../../types/admin";
import { 
  ArrowLeft, 
  Home, 
  FileText, 
  Search,
  Filter,
  Calendar,
  User,
  Activity,
  Download,
  RefreshCw
} from "lucide-react";

export default function ActivityLogs() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AdminLog[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<"today" | "week" | "month" | "all">("all");

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }
    loadLogs();
  }, [user, navigate]);

  useEffect(() => {
    applyFilters();
  }, [logs, searchTerm, filterType, dateFilter]);

  const loadLogs = async () => {
    try {
      const allLogs = await getRecentAdminLogs(500); // Get more logs
      setLogs(allLogs);
    } catch (error) {
      console.error("Error loading logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.targetUserEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.byAdminEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getLogTypeLabel(log.type).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (filterType !== "all") {
      filtered = filtered.filter(log => log.type === filterType);
    }

    // Date filter
    if (dateFilter !== "all") {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      filtered = filtered.filter(log => {
        const logDate = log.createdAt?.toDate?.() || new Date(0);
        if (dateFilter === "today") return logDate >= today;
        if (dateFilter === "week") return logDate >= weekAgo;
        if (dateFilter === "month") return logDate >= monthAgo;
        return true;
      });
    }

    setFilteredLogs(filtered);
  };

  const exportLogs = () => {
    const data = filteredLogs.map(log => ({
      type: getLogTypeLabel(log.type),
      admin: log.byAdminEmail,
      targetUser: log.targetUserEmail,
      metadata: log.meta,
      timestamp: log.createdAt?.toDate?.()?.toISOString() || ""
    }));

    const csv = [
      ["Type", "Admin", "Target User", "Metadata", "Timestamp"],
      ...data.map(row => [
        row.type,
        row.admin,
        row.targetUser,
        JSON.stringify(row.metadata),
        row.timestamp
      ])
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString()}.csv`;
    a.click();
  };

  const logTypes = [
    { value: "all", label: "ทั้งหมด" },
    { value: "SUBSCRIPTION_UPDATE", label: "อัปเดตแพ็กเกจ" },
    { value: "ROLE_CHANGE", label: "เปลี่ยนบทบาท" },
    { value: "INSTRUCTOR_STATUS", label: "เปลี่ยนสถานะผู้สอน" },
    { value: "EMAIL_CHANGE", label: "เปลี่ยนอีเมล" },
    { value: "MAINTENANCE_TOGGLE", label: "เปิด/ปิดระบบ" }
  ];

  const stats = {
    total: logs.length,
    today: logs.filter(log => {
      const logDate = log.createdAt?.toDate?.() || new Date(0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return logDate >= today;
    }).length,
    thisWeek: logs.filter(log => {
      const logDate = log.createdAt?.toDate?.() || new Date(0);
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      return logDate >= weekAgo;
    }).length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark flex items-center gap-3">
                  <FileText className="w-10 h-10 text-gray-600" />
                  บันทึกกิจกรรม
                </h1>
                <p className="text-textLight">ติดตามการเปลี่ยนแปลงและกิจกรรมของแอดมิน</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadLogs}
                className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200"
              >
                <RefreshCw className="w-5 h-5" />
                <span>รีเฟรช</span>
              </button>
              <button
                onClick={exportLogs}
                className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200"
              >
                <Download className="w-5 h-5" />
                <span>ส่งออก</span>
              </button>
              <Link to="/">
                <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                  <Home className="w-5 h-5" />
                  <span>ไปหน้าแรก</span>
                </button>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Activity className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">กิจกรรมทั้งหมด</p>
                  <p className="text-3xl font-bold text-textDark">{stats.total}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">วันนี้</p>
                  <p className="text-3xl font-bold text-green-600">{stats.today}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Activity className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">สัปดาห์นี้</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.thisWeek}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Filters */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-textLight" />
              <input
                type="text"
                placeholder="ค้นหา email หรือประเภทกิจกรรม..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
              />
            </div>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-6 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none font-semibold"
            >
              {logTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            {/* Date Filter */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as any)}
              className="px-6 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none font-semibold"
            >
              <option value="all">ทุกช่วงเวลา</option>
              <option value="today">วันนี้</option>
              <option value="week">สัปดาห์นี้</option>
              <option value="month">เดือนนี้</option>
            </select>
          </div>
        </div>

        {/* Logs List */}
        <Card>
          <div className="p-6">
            <h2 className="text-2xl font-bold text-textDark mb-6">
              บันทึกกิจกรรม ({filteredLogs.length})
            </h2>

            {filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-textLight">ไม่พบบันทึกกิจกรรม</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLogs.map((log) => (
                  <div
                    key={log.id}
                    className="border-2 border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all bg-white"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getLogTypeColor(log.type)}`}>
                            {getLogTypeLabel(log.type)}
                          </span>
                          <span className="text-xs text-textLight">
                            {log.createdAt?.toDate?.()?.toLocaleString('th-TH', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) || 'ไม่ระบุเวลา'}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-sm mb-2">
                          <User className="w-4 h-4 text-blue-600" />
                          <span className="font-semibold text-textDark">{log.byAdminEmail}</span>
                          <span className="text-textLight">→</span>
                          <span className="font-semibold text-textDark">{log.targetUserEmail}</span>
                        </div>

                        {log.meta && Object.keys(log.meta).length > 0 && (
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs font-semibold text-textLight mb-1">รายละเอียด:</p>
                            <div className="text-sm text-textDark space-y-1">
                              {Object.entries(log.meta).map(([key, value]) => (
                                <div key={key} className="flex gap-2">
                                  <span className="font-semibold">{key}:</span>
                                  <span>{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
